import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { useAuth } from '../../../global/contexts/auth/auth';
import { coordinationKeys } from '../queries/keys';
import { coordinationPaths } from '../queries/paths';
import { SOURCE, onFixtures, readSample } from '../queries/source';
import { collectPages, sendBatch } from '../utils/fleet';
import { reviewWorkByPosition } from '../utils/activity';

export function useFleet() {
    const { can } = useAuth();
    return useQuery({
        queryKey: coordinationKeys.fleet(SOURCE),
        enabled: can('machines:read'),
        refetchInterval: onFixtures ? false : 15000,
        refetchIntervalInBackground: false,
        queryFn: async ({ signal }) => {
            if (onFixtures) return readSample('seats').response;
            return collectPages(async params => {
                const response = await api.get(coordinationPaths.seats(), { params, signal });
                return response.data;
            }, row => row.position_id);
        },
    });
}

// QUÉ ESTÁ REVISANDO CADA PUESTO, con lo que Core publica hoy.
//
// La parrilla de puestos arma `work` con `agent_attempts`, y un revisor no toma
// tareas: ahí nunca hay nada. La bandeja de atención, en cambio, trae por cada
// ronda abierta sin decidir el puesto de su reserva viva. Es la única ruta
// publicada que ata un revisor a su revisión, así que la pantalla la lee.
//
// Pide `events:read`, que no es el permiso de la parrilla: sin él, la sección
// sigue funcionando y lo que falta se dice como lo que es, un dato que esta
// cuenta no puede ver.
export function useReviewWork() {
    const { can } = useAuth();
    return useQuery({
        queryKey: coordinationKeys.reviewWork(SOURCE),
        enabled: can('events:read'),
        refetchInterval: onFixtures ? false : 15000,
        refetchIntervalInBackground: false,
        queryFn: async ({ signal }) => {
            if (onFixtures) {
                const sample = readSample('attention').response;
                return reviewWorkByPosition(sample.items ?? []);
            }
            const page = await collectPages(async params => {
                const response = await api.get(coordinationPaths.attention(), {
                    params: { ...params, kind: 'revision_incompleta' },
                    signal,
                });
                return response.data;
            }, item => item.id);
            return reviewWorkByPosition(page.items ?? []);
        },
    });
}

export function useFleetCatalog(kind) {
    const { can } = useAuth();
    return useQuery({
        queryKey: coordinationKeys.catalog(kind),
        enabled: !onFixtures && can(`${kind}:read`),
        staleTime: 60000,
        queryFn: async ({ signal }) => {
            const result = await collectPages(async params => {
                const response = await api.get(coordinationPaths.catalog(kind), { params, signal });
                return response.data;
            });
            return result.items;
        },
    });
}

export function useBatchOrders() {
    const client = useQueryClient();
    return useMutation({
        retry: false,
        mutationFn: ({ entries, onProgress }) => sendBatch(entries, async (positionId, body) => {
            if (onFixtures) return { sent: false };
            await api.post(coordinationPaths.orders(positionId), body);
            return { sent: true };
        }, onProgress),
        onSettled: () => client.invalidateQueries({ queryKey: coordinationKeys.all }),
    });
}
